/**
 * Modo secretaria: dictado con aspecto de procesador de textos.
 *
 * Regla de aislamiento (especificacion 4.4): en modo secretaria Androcles SOLO
 * escribe. Este componente jamas dispara herramientas del agente: solo acumula
 * transcripcion y, al cerrar, llama a `api.cerrarDictado`. Si en el dictado hay
 * criterios contables, es el backend quien los propone luego a la bandeja N2.
 *
 * Diseno (rehecho el 04/08/2026 a peticion del jefe)
 * --------------------------------------------------
 * Antes el texto se pintaba a 72px ocupando el ancho de la columna: cabian
 * cuatro palabras por linea y "hay que hacer scroll en cada fila casi". Ahora
 * es una HOJA: cuerpo de texto pequeno y regulable, ancho de lectura acotado
 * (~78 caracteres, como un folio), margenes generosos y la hoja ocupando
 * practicamente toda la pantalla. Lo que rodea a la hoja (vumetro, contador,
 * botones) se ha comprimido a una barra fina para no robarle sitio.
 *
 * Sigue mandando la edad del usuario en dos cosas:
 *  - El **tamano de letra es suyo**: tres tamanos a un toque (y se recuerda),
 *    porque "pequena" para leer un folio no es lo mismo que para dictarlo de
 *    pie.
 *  - La **escritura a mano siempre esta disponible**. El microfono puede
 *    fallar (permiso, cable, direccion equivocada); el kiosco no se queda mudo
 *    por eso, y el aviso dice que hacer sin una sola palabra tecnica.
 */
import { useEffect, useRef, useState } from 'react';
import { Boton } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ResultadoDictado } from '@/lib/api';
import type { MensajeAmable } from '@/lib/audio';

/** Tamanos de letra de la hoja. En px reales, no en escala del kiosco. */
const TAMANOS = [
  { clave: 'pequena', etiqueta: 'A', px: 15, titulo: 'Letra pequeña' },
  { clave: 'mediana', etiqueta: 'A', px: 19, titulo: 'Letra mediana' },
  { clave: 'grande', etiqueta: 'A', px: 25, titulo: 'Letra grande' },
] as const;

type ClaveTamano = (typeof TAMANOS)[number]['clave'];

/** Donde se recuerda el tamano elegido (por equipo, no por sesion). */
const CLAVE_TAMANO = 'androcles_dictado_tamano';

interface PropsDictado {
  /** Texto ya consolidado (transcripciones finales concatenadas). */
  textoFinal: string;
  /** Fragmento aun en vuelo que faster-whisper todavia puede corregir. */
  textoParcial?: string;
  /** true si el microfono esta capturando. */
  escuchando: boolean;
  /** Volumen del microfono, 0..1. Alimenta el indicador de escucha. */
  nivel?: number;
  /** Fallo del microfono ya traducido a lenguaje de persona. */
  aviso?: MensajeAmable | null;
  alAlternarEscucha: () => void;
  /** Anade texto escrito a mano al mismo dictado. */
  alEscribirAMano?: (texto: string) => void;
  /** Cierra el dictado; el backend guarda la nota en boveda\dictados\. */
  alCerrar: (exportarWord: boolean) => Promise<ResultadoDictado | void>;
  className?: string;
}

export function Dictado({
  textoFinal,
  textoParcial = '',
  escuchando,
  nivel = 0,
  aviso = null,
  alAlternarEscucha,
  alEscribirAMano,
  alCerrar,
  className,
}: PropsDictado) {
  const [cerrando, setCerrando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoDictado | null>(null);
  const [tamano, setTamano] = useState<ClaveTamano>(() => {
    try {
      const guardado = localStorage.getItem(CLAVE_TAMANO);
      return (TAMANOS.find((t) => t.clave === guardado)?.clave ?? 'pequena') as ClaveTamano;
    } catch {
      return 'pequena';
    }
  });
  const finRef = useRef<HTMLSpanElement>(null);

  // Autoscroll continuo: la ultima palabra dicha siempre a la vista.
  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [textoFinal, textoParcial]);

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_TAMANO, tamano);
    } catch {
      // Sin almacenamiento local se pierde la preferencia; nada mas.
    }
  }, [tamano]);

  const cerrar = async (exportarWord: boolean) => {
    setCerrando(true);
    try {
      const res = await alCerrar(exportarWord);
      if (res) setResultado(res);
    } finally {
      setCerrando(false);
    }
  };

  const hayTexto = textoFinal.trim().length > 0;
  const palabras = hayTexto ? textoFinal.trim().split(/\s+/).length : 0;
  const px = TAMANOS.find((t) => t.clave === tamano)?.px ?? 15;

  return (
    <section
      className={cn('flex min-h-0 flex-col gap-3', className)}
      aria-label="Dictado en modo secretaria"
    >
      <BarraHerramientas
        escuchando={escuchando}
        nivel={nivel}
        hayAviso={!!aviso}
        palabras={palabras}
        tamano={tamano}
        alCambiarTamano={setTamano}
      />

      {aviso && <AvisoMicrofono aviso={aviso} />}

      {/*
        La HOJA. Fondo propio y ancho de folio centrado: el texto se lee en
        lineas de ~78 caracteres en vez de cuatro palabras por renglon.
        `min-h-[60vh]` en movil y altura completa en escritorio para que ocupe
        practicamente toda la pantalla, que es lo que se pidio.
      */}
      <div
        className="min-h-[60vh] flex-1 overflow-y-auto rounded-lg border-2 border-border bg-card p-4 sm:p-8 lg:min-h-0"
        aria-live="polite"
        aria-atomic="false"
      >
        <div className="mx-auto w-full max-w-[78ch]">
          {!hayTexto && !textoParcial && (
            <p className="text-base text-muted-foreground">
              Cuando quiera, empiece a hablar. Yo escribo.
            </p>
          )}

          {/*
            Tamano en `style` y no en clases de Tailwind: el kiosco tiene la
            escala tipografica subida (base 22px) para el resto de la interfaz,
            y aqui hace falta el tamano REAL de un documento.
          */}
          <p
            className="whitespace-pre-wrap break-words text-justify"
            style={{ fontSize: `${px}px`, lineHeight: 1.6 }}
          >
            {textoFinal}
            {textoParcial && <span className="text-muted-foreground">{textoParcial}</span>}
            {escuchando && <span className="ml-1 animate-pulse text-accent">▌</span>}
            <span ref={finRef} />
          </p>
        </div>
      </div>

      {/* Comandos por voz reconocidos; se recuerdan porque no se ven. */}
      <p className="shrink-0 text-xs text-muted-foreground">
        Puede decir: «punto y aparte» · «borra la última frase» · «léeme lo que llevamos» ·
        «ya está».
      </p>

      {alEscribirAMano && <EscribirAMano alAnadir={alEscribirAMano} destacado={!!aviso} />}

      <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
        <Boton
          variante={escuchando ? 'destructivo' : 'principal'}
          className="flex-1"
          onClick={alAlternarEscucha}
          disabled={cerrando}
        >
          {escuchando ? 'Pausar' : hayTexto ? 'Seguir dictando' : 'Empezar a dictar'}
        </Boton>
        <Boton
          variante="secundario"
          className="flex-1"
          onClick={() => cerrar(false)}
          disabled={!hayTexto || cerrando}
        >
          {cerrando ? 'Guardando…' : 'Ya está, guárdelo'}
        </Boton>
        <Boton
          variante="contorno"
          className="flex-1"
          onClick={() => cerrar(true)}
          disabled={!hayTexto || cerrando}
        >
          Guardar y hacer Word
        </Boton>
      </div>

      {resultado && <ResumenGuardado resultado={resultado} />}
    </section>
  );
}

/** Numero de barras del vumetro compacto. */
const BARRAS = 12;

/**
 * Barra de herramientas de la hoja: estado del microfono, contador de
 * palabras y tamano de letra, todo en una franja fina.
 *
 * Sustituye al indicador de escucha gigante (bloque de 20rem con doce barras
 * de 5rem): seguia siendo util, pero se comia media pantalla del documento.
 * Lo esencial se conserva —vumetro visible, rotulo literal, color de alto
 * contraste— en un tercio del alto.
 */
function BarraHerramientas({
  escuchando,
  nivel,
  hayAviso,
  palabras,
  tamano,
  alCambiarTamano,
}: {
  escuchando: boolean;
  nivel: number;
  hayAviso: boolean;
  palabras: number;
  tamano: ClaveTamano;
  alCambiarTamano: (t: ClaveTamano) => void;
}) {
  const encendidas = escuchando ? Math.round(Math.min(1, Math.max(0, nivel)) * BARRAS) : 0;
  const oyendo = escuchando && encendidas > 0;

  const rotulo = hayAviso
    ? 'El micrófono no está funcionando'
    : !escuchando
      ? 'Micrófono apagado'
      : oyendo
        ? 'Le estoy oyendo'
        : 'Micro abierto. Hable cuando quiera.';

  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-x-5 gap-y-3 rounded-lg border-2 px-4 py-2.5 transition-colors',
        escuchando && !hayAviso ? 'border-[hsl(var(--cobro))] bg-card' : 'border-border bg-card/40',
      )}
    >
      <div className="flex h-8 w-28 items-end gap-1" aria-hidden="true">
        {Array.from({ length: BARRAS }, (_, i) => {
          const activa = i < encendidas;
          return (
            <span
              key={i}
              className={cn(
                'flex-1 rounded-sm border transition-[height,background-color] duration-100',
                activa
                  ? 'border-[hsl(var(--cobro))] bg-[hsl(var(--cobro))]'
                  : 'border-border bg-transparent',
              )}
              style={{ height: `${35 + (i / (BARRAS - 1)) * 65}%` }}
            />
          );
        })}
      </div>

      <p
        className={cn(
          'text-sm font-bold',
          hayAviso
            ? 'text-destructive'
            : oyendo
              ? 'text-[hsl(var(--cobro))]'
              : 'text-muted-foreground',
        )}
        aria-live="polite"
      >
        {escuchando && !hayAviso && (
          <span
            className="mr-2 inline-block h-3 w-3 rounded-full bg-[hsl(var(--cobro))] align-middle animate-pulse"
            aria-hidden="true"
          />
        )}
        {rotulo}
      </p>

      <span className="ml-auto text-sm text-muted-foreground" aria-live="polite">
        {palabras} palabras
      </span>

      {/* Tamaño de letra del documento. */}
      <div
        className="flex items-center gap-1 rounded-md border border-border/70 p-1"
        role="group"
        aria-label="Tamaño de letra"
      >
        {TAMANOS.map((t) => (
          <button
            key={t.clave}
            type="button"
            title={t.titulo}
            aria-label={t.titulo}
            aria-pressed={tamano === t.clave}
            onClick={() => alCambiarTamano(t.clave)}
            className={cn(
              'rounded px-2.5 py-1 leading-none transition-colors',
              tamano === t.clave
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
            style={{ fontSize: `${Math.round(t.px * 0.8)}px` }}
          >
            {t.etiqueta}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Aviso de microfono caido.
 *
 * Titular en una linea y despues que hacer, en frases cortas. Ni una palabra
 * del navegador: nada de «getUserMedia», «contexto seguro» ni codigos.
 */
function AvisoMicrofono({ aviso }: { aviso: MensajeAmable }) {
  return (
    <div
      className="shrink-0 animate-entrada-suave rounded-lg border-2 border-destructive bg-destructive/10 p-5"
      role="alert"
    >
      <p className="text-base font-bold text-destructive">{aviso.titulo}</p>
      <p className="mt-2 text-sm leading-relaxed">{aviso.consejo}</p>
    </div>
  );
}

/**
 * Entrada por teclado del dictado.
 *
 * Siempre visible (no solo cuando falla la voz): hay dias en que no apetece
 * hablar, y descubrir el teclado justo cuando algo se ha roto es tarde.
 */
function EscribirAMano({
  alAnadir,
  destacado,
}: {
  alAnadir: (texto: string) => void;
  destacado: boolean;
}) {
  const [borrador, setBorrador] = useState('');

  const anadir = () => {
    const texto = borrador.trim();
    if (!texto) return;
    alAnadir(texto);
    setBorrador('');
  };

  return (
    <form
      className="flex shrink-0 items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        anadir();
      }}
    >
      <label className="sr-only" htmlFor="dictado-a-mano">
        Escriba aquí lo que quiera añadir al dictado
      </label>
      <textarea
        id="dictado-a-mano"
        value={borrador}
        onChange={(e) => setBorrador(e.target.value)}
        onKeyDown={(e) => {
          // Enter anade; Mayus+Enter hace parrafo.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            anadir();
          }
        }}
        rows={1}
        placeholder="…o escríbalo aquí y yo lo apunto igual"
        className={cn(
          'min-w-0 flex-1 resize-none rounded-lg border-2 bg-background px-4 py-2.5',
          'text-sm leading-relaxed placeholder:text-muted-foreground',
          destacado ? 'border-destructive' : 'border-input',
        )}
      />
      <Boton type="submit" disabled={!borrador.trim()} aria-label="Añadir al dictado">
        Anadir
      </Boton>
    </form>
  );
}

/**
 * Confirmacion de guardado con las rutas reales.
 *
 * TODO(Fase4): ofrecer aqui la eleccion de viva voz entre version literal y
 *              version pulida, tal como pide la especificacion 4.4.
 */
function ResumenGuardado({ resultado }: { resultado: ResultadoDictado }) {
  return (
    <div className="shrink-0 animate-entrada-suave rounded-lg border-2 border-[hsl(var(--cobro))] p-5">
      <p className="text-base font-semibold">Guardado.</p>
      <p className="mt-2 break-all text-xs text-muted-foreground">{resultado.ruta_nota}</p>
      {resultado.ruta_docx && (
        <p className="mt-1 break-all text-xs text-muted-foreground">{resultado.ruta_docx}</p>
      )}
    </div>
  );
}
